import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Bell } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {
      // not signed in
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const markAllRead = async () => {
    await api.post("/notifications/read");
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-md bg-white border border-stone-200 hover:border-stone-400 transition-colors flex items-center justify-center text-stone-700"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notification-count" className="absolute -top-1 -right-1 bg-emerald-800 text-stone-50 text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center font-semibold font-sans">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 top-12 w-80 bg-white border border-stone-200 rounded-md overflow-hidden z-50">
          <div className="flex items-center justify-between p-4 border-b border-stone-200">
            <div className="font-serif text-lg text-stone-900">Alerts</div>
            {unread > 0 && (
              <button data-testid="mark-read-btn" onClick={markAllRead} className="text-xs font-sans text-emerald-800 hover:underline">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-sm text-stone-500 font-sans text-center">No alerts yet.</div>
            ) : items.map((n) => (
              <div
                key={n.id}
                data-testid={`notif-${n.id}`}
                className={`p-4 border-b border-stone-100 ${!n.read ? "bg-emerald-50/40" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="font-sans text-sm font-semibold text-stone-900">{n.title}</div>
                  <div className="text-[10px] text-stone-400 font-sans shrink-0">{timeAgo(n.created_at)}</div>
                </div>
                <div className="text-xs text-stone-600 font-sans mt-1">{n.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
